import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Filter, Plus, ShieldCheck, Info } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import useUiCopy from "@/hooks/useUiCopy";
import {
  ASSET_TYPES, US_STATES, fmtCurrency, scoreColor, formatCityState,
} from "@/lib/format";
import { ScoreBadge } from "@/components/ScoreBadge";
import NewOpportunityDialog from "@/components/NewOpportunityDialog";
import { ArchiveOpportunityButton } from "@/components/ArchiveOpportunityButton";
import { useAuth } from "@/context/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

export default function Opportunities() {
  const { user } = useAuth();
  const copy = useUiCopy();
  const isAdmin = user?.role && ["admin", "super_admin", "manager"].includes(user.role);
  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalUnfiltered, setTotalUnfiltered] = useState(0);
  const [totalCapped, setTotalCapped] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showTestData, setShowTestData] = useState(false);  // BLOCK 39 Item 2 ,  default OFF
  const [showArchived, setShowArchived] = useState(false);  // BLOCK 173 ,  default OFF
  const [filters, setFilters] = useState({
    asset_type: "",
    state: "",
    county: "",
    city: "",
    min_score: 0,
    max_price: "",
    seller_financing: false,
    distress: false,
    search: "",
  });
  const [showDialog, setShowDialog] = useState(false);
  // BLOCK 131 ,  admin-only "why this score" legend. Fetched from a
  // server-side role-gated endpoint (403s for non-admin), not just hidden
  // client-side.
  const [showLegend, setShowLegend] = useState(false);
  const [legend, setLegend] = useState(null);
  const [legendLoading, setLegendLoading] = useState(false);
  const openLegend = async () => {
    setShowLegend(true);
    if (legend) return;
    setLegendLoading(true);
    try {
      const res = await api.get("/admin/score-legend");
      setLegend(res.data);
    } catch (e) {
      toast.error("Could not load score legend");
      setShowLegend(false);
    } finally {
      setLegendLoading(false);
    }
  };
  // BLOCK 80 ,  cascading County / City. Counties load when State changes;
  // City uses a debounced, index-backed, case-insensitive autocomplete.
  const GEO_UNKNOWN = "__unknown__";
  const [counties, setCounties] = useState([]);
  const [countyUnknown, setCountyUnknown] = useState(0);
  const [cityQuery, setCityQuery] = useState("");
  const [citySuggest, setCitySuggest] = useState([]);
  const [cityUnknown, setCityUnknown] = useState(0);
  const [cityOpen, setCityOpen] = useState(false);
  // BLOCK 29: live-count counter next to the min-score slider. Queries a
  // dedicated cheap endpoint that mirrors the list filter (cf_readiness_score,
  // subscriber_opportunity_filter, PII/visibility gates). Debounced so
  // dragging the slider does not spam the backend.
  const [liveCount, setLiveCount] = useState(null);        // null = not yet fetched
  const [liveCountCapped, setLiveCountCapped] = useState(false);
  const [liveCountLoading, setLiveCountLoading] = useState(false);

  // BLOCK 148 ,  page size control. Defaults to 25 (was an unconditional
  // 500 with no way to change it). Included in `params` so switching sizes
  // resets pagination to page 1 the same way a filter change does.
  // BLOCK 183 Part D ,  500 restored as a selectable (not default) option.
  const [pageSize, setPageSize] = useState(25);

  const params = useMemo(() => {
    const p = { limit: pageSize };
    if (filters.asset_type) p.asset_type = filters.asset_type;
    if (filters.state) p.state = filters.state;
    if (filters.county) p.county = filters.county;
    if (filters.city) p.city = filters.city;
    if (filters.min_score) p.min_score = filters.min_score;
    if (filters.max_price) p.max_price = filters.max_price;
    if (filters.seller_financing) p.seller_financing = true;
    if (filters.distress) p.distress = true;
    if (filters.search) p.search = filters.search;
    if (isAdmin && showTestData) p.include_test = true;
    if (isAdmin && showArchived) p.include_archived = true;
    return p;
  }, [filters, isAdmin, showTestData, showArchived, pageSize]);

  // BLOCK 141 ,  cursor-based pagination. `cursor` describes where the
  // CURRENT page started (null = page 1). `navStack` remembers the cursor
  // used to reach each prior page so "Previous" can step straight back
  // without a reverse query. Changing any filter (params changes) always
  // drops back to page 1 with an empty stack ,  it never silently continues
  // from the old filter's cursor.
  const [cursor, setCursor] = useState(null);
  const [pageNum, setPageNum] = useState(1);
  const [navStack, setNavStack] = useState([]);
  const [hasMore, setHasMore] = useState(false);

  const fetchPage = (curCursor, curPageNum, advancePages = 1) => {
    setLoading(true);
    const p = { ...params };
    if (curCursor) {
      p.after_oid = curCursor.oid;
      if (curCursor.isNull) p.after_score_is_null = true;
      else p.after_score = curCursor.score;
    }
    if (advancePages > 1) p.advance_pages = advancePages;
    api.get("/opportunities", { params: p })
      .then((r) => {
        setItems(r.data);
        // The backend now returns X-Total-Count (filtered) + X-Total-Unfiltered
        // headers so we can show "{shown} of {total} matches" instead of just
        // the page size ,  fixes the missing-totals bug.
        const total = parseInt(r.headers["x-total-count"] || r.headers["X-Total-Count"] || "0", 10);
        const totalAll = parseInt(r.headers["x-total-unfiltered"] || r.headers["X-Total-Unfiltered"] || "0", 10);
        setTotalCount(total || r.data.length);
        setTotalUnfiltered(totalAll || total || r.data.length);
        // BLOCK 182 Item 1/2 ,  a capped count must never render as an
        // exact number (e.g. "10,000 matches" when the real total could
        // be 50,000) ,  the header is the only reliable signal for that.
        setTotalCapped(((r.headers["x-total-capped"] || r.headers["X-Total-Capped"] || "") + "").toLowerCase() === "true");
        setHasMore((r.headers["x-has-more"] || "false").toLowerCase() === "true");
        setCursor(curCursor);
        setPageNum(curPageNum);
      })
      .finally(() => setLoading(false));
  };

  // Any filter change resets pagination to a fresh page 1 ,  never continues
  // from wherever the previous filter's cursor left off.
  useEffect(() => {
    setNavStack([]);
    fetchPage(null, 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const cursorFromLastItem = () => {
    const last = items[items.length - 1];
    if (!last) return null;
    const isNull = last.cf_readiness_score === null || last.cf_readiness_score === undefined;
    return { score: isNull ? null : last.cf_readiness_score, oid: last.oid, isNull };
  };

  const goNext = () => {
    if (!hasMore || items.length === 0) return;
    const nextCursor = cursorFromLastItem();
    setNavStack((s) => [...s, { cursor, pageNum }]);
    fetchPage(nextCursor, pageNum + 1);
  };

  const goAdvance = (n) => {
    if (!hasMore || items.length === 0) return;
    const nextCursor = cursorFromLastItem();
    setNavStack((s) => [...s, { cursor, pageNum }]);
    fetchPage(nextCursor, pageNum + n, n);
  };

  const goPrev = () => {
    if (navStack.length === 0) return;
    const prev = navStack[navStack.length - 1];
    setNavStack((s) => s.slice(0, -1));
    fetchPage(prev.cursor, prev.pageNum);
  };

  // BLOCK 29: debounced live count for the min-score slider. Fires 300ms
  // after the user stops moving the slider (or changes any other filter).
  // Uses the same params object as the list load so the number the user
  // sees is exactly what they'd get if they applied the filters.
  useEffect(() => {
    let cancelled = false;
    setLiveCountLoading(true);
    const t = setTimeout(() => {
      api.get("/opportunities/count", { params })
        .then((r) => {
          if (!cancelled) {
            setLiveCount(r.data?.count ?? 0);
            setLiveCountCapped(!!r.data?.capped);
          }
        })
        .catch(() => {
          if (!cancelled) { setLiveCount(null); setLiveCountCapped(false); }
        })
        .finally(() => {
          if (!cancelled) setLiveCountLoading(false);
        });
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [params]);

  // BLOCK 80 ,  load County options whenever State changes; reset County/City.
  useEffect(() => {
    setFilters((f) => ({ ...f, county: "", city: "" }));
    setCityQuery("");
    setCounties([]); setCountyUnknown(0); setCitySuggest([]); setCityUnknown(0);
    if (!filters.state) return;
    let cancelled = false;
    api.get("/opportunities/facets/counties", { params: { state: filters.state } })
      .then((r) => {
        if (cancelled) return;
        setCounties(r.data?.counties || []);
        setCountyUnknown(r.data?.unknown_count || 0);
      })
      .catch(() => { if (!cancelled) { setCounties([]); setCountyUnknown(0); } });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.state]);

  // BLOCK 80 ,  debounced, index-backed city autocomplete (case-insensitive).
  useEffect(() => {
    if (!filters.state || !cityOpen) return;
    let cancelled = false;
    const t = setTimeout(() => {
      api.get("/opportunities/facets/cities", {
        params: { state: filters.state, q: cityQuery, county: filters.county || undefined },
      })
        .then((r) => {
          if (cancelled) return;
          setCitySuggest(r.data?.cities || []);
          if (r.data?.unknown_count != null) setCityUnknown(r.data.unknown_count);
        })
        .catch(() => { if (!cancelled) setCitySuggest([]); });
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cityQuery, filters.state, filters.county, cityOpen]);

  // BLOCK 39 Item 4 ,  Export CSV was removed from the Opportunities
  // header on 2026-08-12. The dedicated /reports surface already exposes
  // CSV + PDF + scope-selection + GraphQL and is the sanctioned entry
  // point (see routes_core.py:889 /api/export/opportunities.csv and the
  // "Quick Exports" card at /app/reports).


  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-emerald-400 mb-2">Search Engine</div>
          <h1 className="font-heading text-4xl tracking-tight">Acquisition Opportunities</h1>
          <p className="text-sm text-slate-400 mt-1" data-testid="opps-count">
            {totalCount === items.length
              ? `${totalCapped ? "10,000+" : totalCount.toLocaleString()} match${totalCount === 1 ? "" : "es"}`
              : `${items.length} of ${totalCapped ? "10,000+" : totalCount.toLocaleString()} matches`}
            {totalUnfiltered > totalCount && (
              <span className="text-slate-500"> · {totalUnfiltered.toLocaleString()} total opportunities</span>
            )}
            <span className="text-slate-500"> · sorted by CF Readiness</span>
          </p>
        </div>
        <div className="flex gap-2 items-center">
          {isAdmin && (
            <button
              onClick={openLegend}
              data-testid="score-legend-btn"
              className="flex items-center gap-1.5 text-xs text-slate-400 border border-white/10 rounded-sm px-3 py-2 hover:bg-white/[0.03] transition-colors"
              title="Admin-only: why this score"
            >
              <Info className="h-3.5 w-3.5" />
              Why this score?
            </button>
          )}
          {isAdmin && (
            <label
              className="flex items-center gap-2 text-xs text-slate-400 border border-white/10 rounded-sm px-3 py-2 cursor-pointer hover:bg-white/[0.03] transition-colors"
              title="Include is_test=True rows in the list. Admin-only, default off."
              data-testid="opp-test-data-toggle-label"
            >
              <input
                type="checkbox"
                checked={showTestData}
                onChange={(e) => setShowTestData(e.target.checked)}
                data-testid="opp-test-data-toggle"
                className="h-3.5 w-3.5 accent-fuchsia-500"
              />
              <span>Show test data</span>
              {showTestData && (
                <span className="text-[9px] uppercase tracking-widest text-fuchsia-400" data-testid="opp-test-data-on-pill">ON</span>
              )}
            </label>
          )}
          {isAdmin && (
            <label
              className="flex items-center gap-2 text-xs text-slate-400 border border-white/10 rounded-sm px-3 py-2 cursor-pointer hover:bg-white/[0.03] transition-colors"
              title="Show ONLY archived/withdrawn rows. Admin-only, default off."
              data-testid="opp-archived-toggle-label"
            >
              <input
                type="checkbox"
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
                data-testid="opp-archived-toggle"
                className="h-3.5 w-3.5 accent-amber-500"
              />
              <span>Show archived</span>
              {showArchived && (
                <span className="text-[9px] uppercase tracking-widest text-amber-400" data-testid="opp-archived-on-pill">ON</span>
              )}
            </label>
          )}
          <button onClick={() => setShowDialog(true)} data-testid="new-opp-btn" className="btn-primary text-sm inline-flex items-center gap-2">
            <Plus className="h-4 w-4" /> New Opportunity
          </button>
        </div>
      </header>

      {/* Filters ,  BLOCK 141: sticky under the app's top bar (45px) so they
          stay visible while the results list scrolls beneath them. */}
      <div
        className="ae-card p-4 grid grid-cols-2 md:grid-cols-6 gap-3 text-sm sticky top-[45px] z-20 shadow-lg shadow-black/40"
        data-testid="opp-filter-bar"
      >
        <div className="md:col-span-2 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
          <input
            data-testid="opp-search"
            placeholder="Search title, city, description…"
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            className="w-full bg-[#0A0D12] border border-white/10 rounded-sm pl-9 pr-3 py-2 focus:outline-none focus:border-emerald-500/60"
          />
        </div>
        <select
          data-testid="filter-asset"
          value={filters.asset_type}
          onChange={(e) => setFilters({ ...filters, asset_type: e.target.value })}
          className="bg-[#0A0D12] border border-white/10 rounded-sm px-2 py-2"
        >
          <option value="">All Assets</option>
          {ASSET_TYPES.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
        </select>
        <select
          data-testid="filter-state"
          value={filters.state}
          onChange={(e) => setFilters({ ...filters, state: e.target.value })}
          className="bg-[#0A0D12] border border-white/10 rounded-sm px-2 py-2"
        >
          <option value="">All States</option>
          {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        {/* BLOCK 80 ,  County (cascades from State) */}
        <select
          data-testid="filter-county"
          value={filters.county}
          disabled={!filters.state}
          onChange={(e) => setFilters({ ...filters, county: e.target.value, city: "" })}
          className="bg-[#0A0D12] border border-white/10 rounded-sm px-2 py-2 disabled:opacity-40"
          title={filters.state ? "Filter by county" : "Select a state first"}
        >
          <option value="">{filters.state ? "All Counties" : "County (pick state)"}</option>
          {countyUnknown > 0 && (
            <option value={GEO_UNKNOWN} data-testid="filter-county-unknown">
              (Unknown) · {countyUnknown.toLocaleString()}
            </option>
          )}
          {counties.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        {/* BLOCK 80 ,  City autocomplete (cascades from State, index-backed) */}
        <div className="relative">
          <input
            data-testid="filter-city"
            placeholder={filters.state ? "City (type to search)" : "City (pick state)"}
            disabled={!filters.state}
            value={filters.city === GEO_UNKNOWN ? "(Unknown)" : (filters.city || cityQuery)}
            onFocus={() => { setCityOpen(true); }}
            onChange={(e) => {
              setCityQuery(e.target.value);
              setCityOpen(true);
              if (filters.city) setFilters((f) => ({ ...f, city: "" }));
            }}
            onBlur={() => setTimeout(() => setCityOpen(false), 150)}
            className="w-full bg-[#0A0D12] border border-white/10 rounded-sm px-3 py-2 disabled:opacity-40"
          />
          {(filters.city || cityQuery) && (
            <button
              type="button"
              data-testid="filter-city-clear"
              onMouseDown={(e) => { e.preventDefault(); setFilters((f) => ({ ...f, city: "" })); setCityQuery(""); setCityOpen(false); }}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-200 text-xs"
            >✕</button>
          )}
          {cityOpen && filters.state && (
            <div
              data-testid="filter-city-dropdown"
              className="absolute z-20 mt-1 w-full max-h-64 overflow-auto bg-[#0A0D12] border border-white/10 rounded-sm shadow-xl"
            >
              {cityUnknown > 0 && !cityQuery && (
                <button
                  type="button"
                  data-testid="filter-city-unknown"
                  onMouseDown={(e) => { e.preventDefault(); setFilters((f) => ({ ...f, city: GEO_UNKNOWN })); setCityQuery(""); setCityOpen(false); }}
                  className="w-full text-left px-3 py-2 text-xs text-amber-300 hover:bg-white/5 border-b border-white/8"
                >(Unknown) · {cityUnknown.toLocaleString()}</button>
              )}
              {citySuggest.length === 0 ? (
                <div className="px-3 py-2 text-xs text-slate-500">
                  {cityQuery ? "No matching cities" : "Type to search cities…"}
                </div>
              ) : (
                citySuggest.map((ct) => (
                  <button
                    key={ct}
                    type="button"
                    data-testid={`filter-city-option-${ct}`}
                    onMouseDown={(e) => { e.preventDefault(); setFilters((f) => ({ ...f, city: ct })); setCityQuery(ct); setCityOpen(false); }}
                    className="w-full text-left px-3 py-2 text-sm text-slate-200 hover:bg-white/5"
                  >{ct}</button>
                ))
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 md:col-span-2">
          <span className="text-xs text-slate-400 whitespace-nowrap">Min Score</span>
          <input
            data-testid="filter-min-score"
            type="range" min="0" max="100" step="5"
            value={filters.min_score}
            onChange={(e) => setFilters({ ...filters, min_score: +e.target.value })}
            className="flex-1 accent-emerald-500"
          />
          <span className="font-mono w-8 text-right tabular-nums">{filters.min_score}</span>
          {/* BLOCK 29: live counter ,  updates 300ms after the last drag tick.
              Queries the same cf_readiness_score field the slider filters on. */}
          <span
            data-testid="min-score-live-count"
            className={`text-[11px] font-mono tabular-nums whitespace-nowrap min-w-[7rem] text-right ${
              liveCount === 0
                ? "text-amber-400"
                : liveCountLoading
                ? "text-slate-500"
                : "text-slate-300"
            }`}
            title={
              liveCount === 0
                ? "No opportunities at this threshold ,  try lowering Min Score."
                : "Live-updating count of opportunities matching the current filters."
            }
          >
            {liveCountLoading && liveCount === null
              ? "…"
              : liveCount === null
              ? ""
              : liveCount === 0
              ? "0 · none at this threshold"
              : liveCountCapped
              ? "10,000+ matches"
              : `${liveCount.toLocaleString()} match${liveCount === 1 ? "" : "es"}`}
          </span>
        </div>
        <input
          data-testid="filter-max-price"
          type="number" placeholder="Max price"
          value={filters.max_price}
          onChange={(e) => setFilters({ ...filters, max_price: e.target.value })}
          className="bg-[#0A0D12] border border-white/10 rounded-sm px-3 py-2 font-mono"
        />
        <div className="md:col-span-6 flex flex-wrap gap-3 text-xs">
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input
              data-testid="filter-sf"
              type="checkbox"
              checked={filters.seller_financing}
              onChange={(e) => setFilters({ ...filters, seller_financing: e.target.checked })}
              className="accent-emerald-500"
            />
            <span>Seller Financing ≥60%</span>
          </label>
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input
              data-testid="filter-distress"
              type="checkbox"
              checked={filters.distress}
              onChange={(e) => setFilters({ ...filters, distress: e.target.checked })}
              className="accent-emerald-500"
            />
            <span>Distressed ≥60%</span>
          </label>
        </div>
      </div>

      {/* Table */}
      <div className="ae-card overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] uppercase tracking-widest text-slate-500 border-b border-white/8">
              <th className="text-left px-4 py-3">Score</th>
              <th className="text-left px-4 py-3">Opportunity</th>
              <th className="text-left px-4 py-3">Location</th>
              <th className="text-right px-4 py-3">Asking</th>
              <th className="text-left px-4 py-3">Signals</th>
              {isAdmin && <th className="text-left px-4 py-3 hidden lg:table-cell">Source</th>}
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="text-center text-slate-500 py-8">Loading…</td></tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={7} className="text-center py-10" data-testid="opps-empty-state">
                  <div className="text-slate-300 text-sm font-medium">
                    {filters.min_score >= 60
                      ? "No opportunities at this threshold"
                      : "No opportunities match these filters"}
                  </div>
                  <div className="text-slate-500 text-xs mt-2">
                    {filters.min_score >= 60
                      ? `Nothing scored ≥ ${filters.min_score} matches your other filters. Try lowering Min Score.`
                      : "Adjust the filters above to broaden the search."}
                  </div>
                  {filters.min_score > 0 && (
                    <button
                      type="button"
                      onClick={() => setFilters({ ...filters, min_score: 0 })}
                      data-testid="clear-min-score-btn"
                      className="btn-ghost text-xs mt-3 inline-flex items-center gap-1"
                    >
                      Reset Min Score to 0
                    </button>
                  )}
                </td>
              </tr>
            ) : (
              items.map((o) => (
                <tr
                  key={o.oid}
                  data-testid={`opp-row-${o.oid}`}
                  className="border-b border-white/5 hover:bg-white/[0.03] transition-colors"
                >
                  <td className="px-4 py-3">
                    {/* BLOCK 25: authoritative score is the top-level v2.1 cf_readiness_score
                        (99.7% populated, drives pipeline/hot-lead/verified-owner-direct badge).
                        ai_score.opportunity_score (LLM narrative gut-check) stays as a smaller
                        secondary chip below only when populated (~0.02% of rows today). */}
                    <div
                      className={`font-mono text-xl font-bold tabular-nums ${scoreColor(o.cf_readiness_score || 0)}`}
                      data-testid={`opp-cf-score-${o.oid}`}
                      title="CF Readiness Score (v2.1) ,  6-dimension rules-based score used by the pipeline."
                    >
                      {o.cf_readiness_score ?? 0}
                    </div>
                    {(o.ai_score?.opportunity_score || 0) > 0 && (
                      <div
                        className="mt-0.5 font-mono text-[10px] text-slate-500 tabular-nums"
                        data-testid={`opp-ai-score-${o.oid}`}
                        title="Claude Sonnet qualitative gut-check ,  secondary opinion only."
                      >
                        AI {o.ai_score.opportunity_score}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <Link to={`/app/opportunities/${o.oid}`} className="font-medium hover:text-emerald-400">{o.title}</Link>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <div className="text-[11px] text-slate-500 capitalize">{o.asset_type}</div>
                      {o.verified_direct_seller && (
                        <span
                          className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest text-emerald-300 border border-emerald-500/40 bg-emerald-500/10 rounded-sm px-1.5 py-0.5"
                          data-testid={`opp-verified-direct-badge-${o.oid}`}
                          title="Owner submitted this listing directly and it was reviewed by our team."
                        >
                          <ShieldCheck className="h-2.5 w-2.5" strokeWidth={2.2} />
                          Verified Direct
                        </span>
                      )}
                      {o.scoring_stage === "first_pass_fdor" && (
                        <span
                          className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest text-amber-300/90 border border-amber-500/40 bg-amber-500/10 rounded-sm px-1.5 py-0.5"
                          data-testid={`opp-first-pass-badge-${o.oid}`}
                          title={copy.first_pass_tooltip_short}
                        >
                          First-pass
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-300" data-testid="opp-row-location">
                    {o.city || o.state ? (
                      <>
                        {(o.city || "").trim() && <>{o.city}</>}
                        {(o.city || "").trim() && (o.state || "").trim() && <>, </>}
                        {(o.state || "").trim() && <span className="font-mono">{o.state}</span>}
                      </>
                    ) : (
                      <span className="text-slate-500">, </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right font-mono tabular-nums">{fmtCurrency(o.asking_price)}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1.5">
                      <ScoreBadge score={o.ai_score?.seller_financing_probability || 0} label="SF" size="sm" />
                      <ScoreBadge score={o.ai_score?.master_lease_probability || 0} label="ML" size="sm" />
                      <ScoreBadge score={o.ai_score?.distress_probability || 0} label="DI" size="sm" />
                    </div>
                  </td>
                  {isAdmin && <td className="px-4 py-3 hidden lg:table-cell text-xs text-slate-500">{o.source}</td>}
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2 justify-end">
                      {isAdmin && (
                        <ArchiveOpportunityButton
                          oid={o.oid}
                          archived={o.admin_archived || o.withdrawn}
                          onChanged={() => fetchPage(cursor, pageNum)}
                        />
                      )}
                      <Link
                        to={`/app/opportunities/${o.oid}`}
                        data-testid={`opp-view-${o.oid}`}
                        className="text-emerald-400 hover:text-emerald-300 text-xs"
                      >
                        View →
                      </Link>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* BLOCK 141 ,  cursor-based pagination. Sequential Next/Previous is
          always cheap. "+5 / +10 pages" batch-advances from the CURRENT
          cursor (bounded skip, flat cost) ,  a true "jump to page N" isn't
          offered because that would need an offset from row zero, which is
          exactly the unbounded-skip cost this design avoids. */}
      <div className="flex items-center justify-between gap-3 text-xs text-slate-400" data-testid="opp-pagination-bar">
        <div className="flex items-center gap-3">
          <div data-testid="opp-page-indicator">Page {pageNum.toLocaleString()}</div>
          <label className="flex items-center gap-1.5" htmlFor="opp-page-size-select">
            Rows per page
            <select
              id="opp-page-size-select"
              data-testid="opp-page-size-select"
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              disabled={loading}
              className="bg-white/5 border border-white/10 rounded px-2 py-1 text-xs text-slate-200"
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={500}>500</option>
            </select>
          </label>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            data-testid="opp-page-prev"
            onClick={goPrev}
            disabled={navStack.length === 0 || loading}
            className="btn-ghost text-xs px-3 py-1.5 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            ← Previous
          </button>
          <button
            type="button"
            data-testid="opp-page-next"
            onClick={goNext}
            disabled={!hasMore || loading}
            className="btn-ghost text-xs px-3 py-1.5 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            Next →
          </button>
          <span className="w-px h-4 bg-white/10" />
          <button
            type="button"
            data-testid="opp-page-advance-5"
            onClick={() => goAdvance(5)}
            disabled={!hasMore || loading}
            title="Skip ahead 5 pages from here (bounded cost, not a jump from the start)"
            className="btn-ghost text-xs px-3 py-1.5 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            +5 pages
          </button>
          <button
            type="button"
            data-testid="opp-page-advance-10"
            onClick={() => goAdvance(10)}
            disabled={!hasMore || loading}
            title="Skip ahead 10 pages from here (bounded cost, not a jump from the start)"
            className="btn-ghost text-xs px-3 py-1.5 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            +10 pages
          </button>
        </div>
      </div>

      {showDialog && (
        <NewOpportunityDialog
          onClose={() => setShowDialog(false)}
          onCreated={() => { setShowDialog(false); setNavStack([]); fetchPage(null, 1); }}
        />
      )}

      <Dialog open={showLegend} onOpenChange={setShowLegend}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="score-legend-dialog">
          <DialogHeader>
            <DialogTitle>Why this score ,  admin reference</DialogTitle>
            <DialogDescription>Internal detail. Never shown to subscribers.</DialogDescription>
          </DialogHeader>
          {legendLoading && <p className="text-sm text-slate-400">Loading...</p>}
          {legend && (
            <div className="space-y-5 text-sm text-slate-300" data-testid="score-legend-content">
              <section>
                <h3 className="text-xs uppercase tracking-widest text-emerald-400 mb-2">Scoring stages</h3>
                {Object.entries(legend.scoring_stages).map(([key, v]) => (
                  <div key={key} className="mb-2">
                    <div className="font-semibold text-white">{v.label}</div>
                    <div className="text-slate-400">{v.applies_to}</div>
                    <div className="font-mono text-xs text-fuchsia-300 mt-1">{v.formula}</div>
                    {v.why && <div className="text-slate-400 mt-1">{v.why}</div>}
                  </div>
                ))}
              </section>
              <section>
                <h3 className="text-xs uppercase tracking-widest text-emerald-400 mb-2">Why the real ceiling is low</h3>
                <div>Discovery pool ceiling: <span className="text-white font-semibold">{legend.why_the_real_ceiling_is_low.current_real_ceiling_discovery_pool}</span></div>
                <div>Whole pool (incl. legacy test leads): <span className="text-white font-semibold">{legend.why_the_real_ceiling_is_low.current_real_ceiling_whole_pool}</span></div>
                <div className="text-slate-400 mt-1">{legend.why_the_real_ceiling_is_low.root_cause}</div>
              </section>
              <section>
                <h3 className="text-xs uppercase tracking-widest text-emerald-400 mb-2">The six dimensions</h3>
                {Object.entries(legend.dimensions).map(([key, v]) => (
                  <div key={key} className="mb-1.5">
                    <span className="font-semibold text-white capitalize">{key.replace("_", " ")}</span>
                    <span className="text-slate-500"> (max {v.max})</span>
                    <span className="text-slate-400"> ,  {v.measures}</span>
                  </div>
                ))}
              </section>
              <section>
                <h3 className="text-xs uppercase tracking-widest text-emerald-400 mb-2">Compound signals</h3>
                <div className="text-slate-400">{legend.compound_signals_examples.note}</div>
              </section>
              <section>
                <h3 className="text-xs uppercase tracking-widest text-emerald-400 mb-2">asset_suitability vs. cf_readiness_score</h3>
                <div className="mb-1.5"><span className="font-semibold text-white">asset_suitability</span> ,  {legend.asset_suitability_vs_cf_readiness_score.asset_suitability}</div>
                <div><span className="font-semibold text-white">cf_readiness_score</span> ,  {legend.asset_suitability_vs_cf_readiness_score.cf_readiness_score}</div>
              </section>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
